import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Minus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import type { Criterion, CriteriaCategory } from "./mockSelectionData";

const COLOR_MAP: Record<CriteriaCategory["color"], {
  band: string;
  header: string;
  border: string;
  stepperActive: string;
  stepperHover: string;
  valueBg: string;
}> = {
  blue:   { band: "bg-blue-600",   header: "bg-blue-50",   border: "border-blue-200",  stepperActive: "bg-blue-600 text-white",   stepperHover: "hover:bg-blue-50",   valueBg: "bg-blue-100 text-blue-800" },
  green:  { band: "bg-green-600",  header: "bg-green-50",  border: "border-green-200", stepperActive: "bg-green-600 text-white",  stepperHover: "hover:bg-green-50",  valueBg: "bg-green-100 text-green-800" },
  purple: { band: "bg-purple-600", header: "bg-purple-50", border: "border-purple-200",stepperActive: "bg-purple-600 text-white", stepperHover: "hover:bg-purple-50", valueBg: "bg-purple-100 text-purple-800" },
  amber:  { band: "bg-amber-500",  header: "bg-amber-50",  border: "border-amber-200", stepperActive: "bg-amber-500 text-white",  stepperHover: "hover:bg-amber-50",  valueBg: "bg-amber-100 text-amber-800" },
};

interface Props {
  category: string;
  subcategory?: string;
  color: CriteriaCategory["color"];
  criteria: Criterion[];
  scores: Record<string, number>;
  onScoreChange: (key: string, value: number) => void;
  justifications: Record<string, string>;
  onJustificationChange: (key: string, value: string) => void;
}

export function CategorySection({
  category,
  subcategory,
  color,
  criteria,
  scores,
  onScoreChange,
  justifications,
  onJustificationChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const c = COLOR_MAP[color];

  function toggleNote(key: string) {
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  const title = subcategory ? `${category} — ${subcategory}` : category;
  const avgScore =
    criteria.reduce((sum, cr) => sum + (scores[cr.key] ?? 3), 0) / criteria.length;

  return (
    <div
      className={`rounded-xl border ${c.border} overflow-hidden`}
      data-testid={`category-section-${category.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {/* Header — always visible */}
      <button
        className={`w-full flex items-center gap-3 px-4 py-3 ${c.header} text-left`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={`w-1 self-stretch rounded-full ${c.band}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800">{title}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {criteria.length} criteria · avg {avgScore.toFixed(1)}/5
          </p>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
        )}
      </button>

      {/* Body — collapsible */}
      {open && (
        <div className="divide-y divide-gray-100 bg-white">
          {criteria.map((criterion) => {
            const value = scores[criterion.key] ?? 3;
            const noteOpen = expandedNotes.has(criterion.key);

            return (
              <div key={criterion.key} className="px-4 py-3">
                <div className="flex items-center gap-3">
                  {/* Criterion label */}
                  <span className="flex-1 text-sm text-gray-700 min-w-0">
                    {criterion.label}
                  </span>

                  {/* Note toggle */}
                  <button
                    onClick={() => toggleNote(criterion.key)}
                    className="text-xs text-gray-400 hover:text-gray-600 shrink-0 underline underline-offset-2"
                    data-testid={`note-toggle-${criterion.key}`}
                  >
                    {noteOpen ? "hide note" : "+ note"}
                  </button>

                  {/* Stepper — mirrors PR-A2 batch-entry pattern */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => onScoreChange(criterion.key, Math.max(1, value - 1))}
                      className={`w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 ${c.stepperHover} transition-colors`}
                      data-testid={`stepper-minus-${criterion.key}`}
                      aria-label={`Decrease ${criterion.label}`}
                    >
                      <Minus className="h-3 w-3" />
                    </button>

                    <span
                      className={`w-8 h-7 rounded-md flex items-center justify-center text-sm font-bold ${c.valueBg}`}
                      data-testid={`stepper-value-${criterion.key}`}
                    >
                      {value}
                    </span>

                    <button
                      onClick={() => onScoreChange(criterion.key, Math.min(5, value + 1))}
                      className={`w-7 h-7 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 ${c.stepperHover} transition-colors`}
                      data-testid={`stepper-plus-${criterion.key}`}
                      aria-label={`Increase ${criterion.label}`}
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                </div>

                {/* Justification textarea — inline, collapsed by default */}
                {noteOpen && (
                  <Textarea
                    className="mt-2 text-xs resize-none h-16"
                    placeholder={`Note for ${criterion.label}…`}
                    value={justifications[criterion.key] ?? ""}
                    onChange={(e) => onJustificationChange(criterion.key, e.target.value)}
                    data-testid={`note-input-${criterion.key}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
